import axios from 'axios';
import qs from 'qs';

import {
  getPersistedObject,
  persistUser,
  removePersistedObject,
} from '../utils/localStorage';
import { MS_OAUTH_ENDPOINT } from '../variables/GlobalVariables';

const request = axios.create();

const TOKEN_URL = '/v2/oauth/token';

// Single-flight : une rafale de requêtes au jeton expiré ne déclenche qu'un refresh
let refreshPromise: Promise<void> | null = null;

function refreshTokens(): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(
        `${MS_OAUTH_ENDPOINT}${TOKEN_URL}`,
        qs.stringify({
          grant_type: 'refresh_token',
          refresh_token: getPersistedObject('refresh_token')!,
        })
      )
      .then((rs) => {
        persistUser(undefined, rs.data.access_token, rs.data.refresh_token);
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

function logout() {
  // Logging out the user by removing all the tokens from local
  removePersistedObject('user');
  removePersistedObject('access_token');
  removePersistedObject('refresh_token');
  // Redirecting the user to the landing page
  window.location.href = window.location.origin;
}

// Les endpoints protégés par @FeaturePermission seul répondent 403, et non 401, à un
// jeton expiré : on ne peut pas compter sur la réponse pour déclencher le refresh.
export function isTokenExpired(token: string): boolean {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(payload));
    return typeof exp === 'number' && exp * 1000 < Date.now() + 30_000;
  } catch (_ignored) {
    return false;
  }
}

// Adding Authorization header for all requests, refreshing the token first if expired
request.interceptors.request.use(
  async (config) => {
    if (
      !config.url?.includes(TOKEN_URL) &&
      getPersistedObject('refresh_token') &&
      isTokenExpired(getPersistedObject('access_token') ?? '')
    ) {
      try {
        await refreshTokens();
      } catch (_error) {
        logout();
        return Promise.reject(_error);
      }
    }
    const token = getPersistedObject('access_token');
    if (token) {
      config.headers!['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Refresh and relaunch request with same params when access token was expired
request.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalConfig = err.config;

    if (!originalConfig.url.includes(TOKEN_URL) && err.response) {
      // Access Token was expired
      if (
        (err.response.status === 406 || err.response.status === 401) &&
        !originalConfig._retry
      ) {
        originalConfig._retry = true;
        try {
          await refreshTokens();
          return request(originalConfig);
        } catch (_error) {
          logout();
          return Promise.reject(_error);
        }
      }
    }

    return Promise.reject(err);
  }
);

export default request;
